import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, ShieldX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/Field";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const PROGRAM_HI = { AI: "AI", Robotics: "रोबोटिक्स", UAV: "UAV / ड्रोन", Aerospace: "एयरोस्पेस" };

export default function VerifyCertificate() {
  const { L } = useLang();
  const [params] = useSearchParams();
  const [no, setNo] = useState(params.get("no") || "");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (value) => {
    if (!value.trim()) return;
    setLoading(true); setRes(null);
    try {
      const { data } = await api.get(`/certificates/verify/${encodeURIComponent(value.trim())}`);
      setRes({ ok: true, data });
    } catch (e) { setRes({ ok: false, limited: e.response?.status === 429 }); } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("no")) check(params.get("no")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-[#E8F0FE] min-h-[75vh]" data-testid="verify-page">
      <div className="max-w-2xl mx-auto px-5 py-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2E63]">{L("Certificate Check", "प्रमाणपत्र जाँच")}</span>
        <h1 className="text-4xl font-extrabold text-[#0A192F] mt-2 leading-tight">{L("Verify a RoboSpark certificate", "RoboSpark प्रमाणपत्र सत्यापित करें")}</h1>
        <p className="text-slate-600 mt-2">{L("Schools, colleges and employers can confirm a certificate is genuine. Enter the number printed at the bottom-left of the certificate.", "स्कूल, कॉलेज और नियोक्ता प्रमाणपत्र की असलियत जाँच सकते हैं। प्रमाणपत्र के नीचे बाईं ओर छपा नंबर डालें।")}</p>
        <form onSubmit={(e) => { e.preventDefault(); check(no); }} className="mt-8 bg-white p-6 rounded-2xl border border-slate-200 grid sm:grid-cols-[1fr_auto] gap-4 items-end" data-testid="verify-form">
          <Field id="cert_no" label={L("Certificate number", "प्रमाणपत्र संख्या")} required placeholder="RS-2026-XXXX-XXXX-XXXX" value={no} onChange={(e) => setNo(e.target.value)} />
          <Button type="submit" disabled={loading} className="rs-cta h-11 rounded-full px-8" data-testid="verify-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Verify", "जाँचें")}
          </Button>
        </form>
        {res?.ok && (
          <div className="mt-8 bg-white rounded-2xl border-2 border-green-500 p-6" data-testid="verify-valid">
            <div className="flex items-center gap-3 text-emerald-700"><ShieldCheck className="w-8 h-8" /><span className="text-xl font-extrabold">{L("Genuine certificate", "असली प्रमाणपत्र")}</span></div>
            <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
              {[[L("Certificate no.", "प्रमाणपत्र संख्या"), res.data.certificate_no], [L("Student", "छात्र"), res.data.student_name],
                [L("Program", "कार्यक्रम"), L(res.data.program, PROGRAM_HI[res.data.program])], [L("Completed on", "पूर्ण तिथि"), new Date(res.data.completed_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })]]
                .map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="font-bold text-[#0A192F]" data-testid={`verify-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd></div>)}
            </dl>
          </div>
        )}
        {res?.limited && (
          <div className="mt-8 bg-amber-500/15 rounded-2xl border-2 border-amber-400/50 p-6 text-amber-700 font-semibold" data-testid="verify-rate-limited">
            {L("Too many checks from your network. Please wait a minute and try again.", "आपके नेटवर्क से बहुत ज़्यादा जाँच हुईं। कृपया एक मिनट बाद फिर कोशिश करें।")}
          </div>
        )}
        {res && !res.ok && !res.limited && (
          <div className="mt-8 bg-white rounded-2xl border-2 border-red-400 p-6 flex gap-3 items-start" data-testid="verify-invalid">
            <ShieldX className="w-8 h-8 text-red-600 shrink-0" />
            <div><div className="text-xl font-extrabold text-red-600">{L("Not found", "नहीं मिला")}</div>
              <p className="text-sm text-slate-600 mt-1">{L("No valid RoboSpark certificate has this number. Check for typos or contact us at robospark.prp@gmail.com.", "इस नंबर का कोई मान्य RoboSpark प्रमाणपत्र नहीं है। वर्तनी जाँचें या robospark.prp@gmail.com पर संपर्क करें।")}</p></div>
          </div>
        )}
      </div>
    </div>
  );
}
